/**
 * useOutlinerCommands.ts - React hook for Outlinr command registration
 * 
 * CHANGE HISTORY:
 * - 251115_2235 [pending] danielbeer: Create React hook for command registration
 * 
 * Automatically registers all Outlinr commands when component mounts.
 */

import { useEffect, useRef } from 'react';
import { useCommandr } from '@opcentral/commandr';
import { OutlinerCommandRegistry } from '../commands';
import type { OutlinerContext } from '../commands';

/**
 * Hook to register all Outlinr commands
 * 
 * @param context - Outliner context with all dependencies
 * @param enabled - Whether commands should be active (default: true)
 */
export const useOutlinerCommands = (context: OutlinerContext, enabled: boolean = true) => {
  const commandr = useCommandr();
  const registryRef = useRef<OutlinerCommandRegistry | null>(null);
  
  useEffect(() => {
    if (!enabled) return;
    
    // Create registry
    const registry = new OutlinerCommandRegistry(commandr, context);
    registryRef.current = registry;
    
    // Register all commands
    registry.registerAll();
    
    // Cleanup on unmount
    return () => {
      registry.unregisterAll();
      registryRef.current = null;
    };
  }, [commandr, context, enabled]);
  
  return registryRef.current;
};
